import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import Pagination from '../../../components/common/Pagination';
import RichEditor from '../../../components/common/RichEditor';
import { getAllFaqs, createFaq, updateFaq, deleteFaq } from '../../../services/faqService';

const PER_PAGE = 20;

const emptyForm = { category: '', question: '', answer: '', isPublished: true };

const FaqList = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [page, setPage] = useState(1);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getAllFaqs();
      setFaqs(data);
    } catch {
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const totalPages = Math.max(1, Math.ceil(faqs.length / PER_PAGE));
  const paged = useMemo(() => faqs.slice((page - 1) * PER_PAGE, page * PER_PAGE), [faqs, page]);

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...emptyForm, category: '', isPublished: true });
    setShowModal(true);
  };

  const openEdit = (faq) => {
    setEditTarget(faq);
    setForm({
      category: faq.category || '',
      question: faq.question || '',
      answer: faq.answer || '',
      isPublished: faq.isPublished !== false,
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.question || !form.answer) {
      alert('질문과 답변을 입력해 주세요.');
      return;
    }
    try {
      if (editTarget) {
        await updateFaq(editTarget.id, {
          category: form.category || '',
          question: form.question,
          answer: form.answer,
          isPublished: form.isPublished,
        });
      } else {
        await createFaq({
          category: form.category || '',
          question: form.question,
          answer: form.answer,
          isPublished: form.isPublished,
          sortOrder: faqs.length,
        });
      }
      setShowModal(false);
      setEditTarget(null);
      setForm(emptyForm);
      await fetchData();
    } catch (err) {
      alert('저장 실패: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await deleteFaq(id);
      await fetchData();
    } catch (err) {
      alert('삭제 실패: ' + err.message);
    }
  };

  const togglePublish = async (faq) => {
    try {
      await updateFaq(faq.id, { isPublished: faq.isPublished === false });
      await fetchData();
    } catch (err) {
      alert('변경 실패: ' + err.message);
    }
  };

  return (
    <Container>
      <Header>
        <TitleBox>
          <Title>FAQ</Title>
          <Location>노출 위치: 랜딩 푸터(FAQ) · 마이페이지 &gt; FAQ</Location>
        </TitleBox>
        <HeaderActions>
          <TextBtn onClick={fetchData}>새로고침</TextBtn>
          <PrimaryBtn onClick={openCreate}>FAQ 추가</PrimaryBtn>
        </HeaderActions>
      </Header>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: 60 }}>번호</Th>
              <Th style={{ width: 140 }}>카테고리</Th>
              <Th>질문</Th>
              <Th style={{ width: 110 }}>공개</Th>
              <Th style={{ width: 140 }}>관리</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><Td colSpan={5} style={{ textAlign: 'center', padding: '40px 0' }}>로딩 중...</Td></tr>
            ) : paged.length === 0 ? (
              <tr><Td colSpan={5} style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF' }}>등록된 FAQ가 없습니다.</Td></tr>
            ) : paged.map((faq, i) => {
              const published = faq.isPublished !== false;
              return (
                <tr key={faq.id}>
                  <Td style={{ color: '#9CA3AF' }}>{(page - 1) * PER_PAGE + i + 1}</Td>
                  <Td>{faq.category || '-'}</Td>
                  <Td>{faq.question}</Td>
                  <Td>
                    <PubState $on={published} onClick={() => togglePublish(faq)}>
                      {published ? '공개' : '비공개'}
                    </PubState>
                  </Td>
                  <Td>
                    <InlineActions>
                      <TextBtn onClick={() => openEdit(faq)}>수정</TextBtn>
                      <DangerText onClick={() => handleDelete(faq.id)}>삭제</DangerText>
                    </InlineActions>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Pagination current={page} total={totalPages} onChange={setPage} />
      </TableWrap>

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editTarget ? 'FAQ 수정' : 'FAQ 추가'}</ModalTitle>
              <CloseBtn onClick={() => setShowModal(false)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <Field>
                <FieldLabel>카테고리</FieldLabel>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="예: 가입, 서비스, 정산, 기타"
                />
              </Field>
              <Field>
                <FieldLabel>질문</FieldLabel>
                <Input
                  value={form.question}
                  onChange={(e) => setForm({ ...form, question: e.target.value })}
                  placeholder="질문을 입력하세요"
                />
              </Field>
              <Field>
                <FieldLabel>답변</FieldLabel>
                <RichEditor
                  value={form.answer}
                  onChange={(html) => setForm({ ...form, answer: html })}
                  placeholder="답변을 입력하세요"
                  minHeight={200}
                />
              </Field>
              <CheckRow>
                <Check
                  type="checkbox"
                  id="faq-published"
                  checked={form.isPublished}
                  onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
                />
                <label htmlFor="faq-published">
                  <strong>공개</strong>
                  <span> — 체크 해제 시 고객/마이페이지에 표시되지 않습니다.</span>
                </label>
              </CheckRow>
            </ModalBody>
            <ModalFooter>
              <TextBtn onClick={() => setShowModal(false)}>취소</TextBtn>
              <PrimaryBtn onClick={handleSubmit}>{editTarget ? '저장' : '등록'}</PrimaryBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </Container>
  );
};

export default FaqList;

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;
  flex-wrap: wrap;
`;

const TitleBox = styled.div``;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const Location = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const TextBtn = styled.button`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: none;
  padding: 4px 0;
  &:hover:not(:disabled) { color: ${({ theme }) => theme.colors.primary}; text-decoration: underline; }
  &:disabled { color: ${({ theme }) => theme.colors.textLight}; cursor: default; }
`;

const DangerText = styled(TextBtn)`
  color: ${({ theme }) => theme.colors.danger};
  &:hover:not(:disabled) { color: ${({ theme }) => theme.colors.danger}; text-decoration: underline; }
`;

const PrimaryBtn = styled.button`
  padding: 8px 18px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  font-weight: 500;
`;

const TableWrap = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 13px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  vertical-align: middle;
`;

const PubState = styled.button`
  background: none;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.textLight)};
  &:hover { text-decoration: underline; }
`;

const InlineActions = styled.div`
  display: flex;
  gap: 12px;
`;

const Check = styled.input`
  width: 14px;
  height: 14px;
  cursor: pointer;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
`;

const CloseBtn = styled.button`
  color: ${({ theme }) => theme.colors.textLight};
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  align-items: center;
  padding: 14px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const CheckRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  label { cursor: pointer; }
  strong { color: ${({ theme }) => theme.colors.text}; font-weight: 500; }
`;
